import { ChatDivider, ChatMessageList, Spinner } from '@wundo/ui';
import type { ConversationOverview, Message } from '@/types/messages.ts';
import { formatMessageDate } from '@/utils/messageUtils.ts';
import { DirectorDecisionNote } from './DirectorDecisionNote.tsx';
import { FindingNote } from './FindingNote.tsx';
import { MessageDisplay } from './MessageDisplay.tsx';
import { messageElementId } from './messageElementId.ts';
import type { PlacedDirectorDecisions } from './placeDirectorDecisions.ts';
import type { PlacedFinding } from './placeFindings.ts';

export interface TranscriptMessagesProps {
  messages: Message[];
  loadingMore: boolean;
  /** The messages that start another conversation, by index. */
  switches: Map<number, ConversationOverview>;
  director: PlacedDirectorDecisions;
  notesByMessage: Map<string, PlacedFinding[]>;
  currentFinding: string | null;
  showInternal: boolean;
  onOpenMemories: (userId: string, handles: string[]) => void;
}

/** The messages on screen, with the findings and director decisions placed between them. */
export function TranscriptMessages({
  messages,
  loadingMore,
  switches,
  director,
  notesByMessage,
  currentFinding,
  showInternal,
  onOpenMemories,
}: TranscriptMessagesProps) {
  return (
    <ChatMessageList>
      {loadingMore && (
        <div className='flex justify-center py-2'>
          <Spinner className='size-4' />
        </div>
      )}

      {director.leading.map((decision) => (
        <DirectorDecisionNote key={`director-${decision.id}`} decision={decision} />
      ))}

      {messages.map((message, index) => (
        <div key={message.id} className='flex flex-col gap-3.5'>
          {switches.has(index) && (
            <ChatDivider data-ui='conversation-divider'>
              New conversation · {formatMessageDate(message.created_at)}
            </ChatDivider>
          )}
          <MessageDisplay
            id={messageElementId(String(message.id))}
            message={message}
            debug={showInternal}
            onOpenMemories={(handles) => onOpenMemories(message.user_id, handles)}
            notes={notesByMessage.get(String(message.id))?.map((placed) => (
              <FindingNote
                key={placed.key}
                placed={placed}
                current={placed.key === currentFinding}
                author={
                  message.role === 'user' || message.type === 'incoming_message'
                    ? 'user'
                    : 'wundo'
                }
              />
            ))}
          />
          {director.after.get(String(message.id))?.map((decision) => (
            <DirectorDecisionNote key={decision.id} decision={decision} />
          ))}
        </div>
      ))}
    </ChatMessageList>
  );
}
